import { useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { DOMAIN_LABELS } from '@/api/sat';
import { CalloutPin, SurfaceFrame } from '@/components/SatRealScreen';
import { STUDENT_BAND_BAR_CLASSES, STUDENT_BAND_LABELS } from '@/components/sat/journey/studentCopy';
import { demoDomainMap } from '@/pages/open/openDemoDtos';

/**
 * The results surface for /what-we-do/sat/about: the student's course home after the diagnostic,
 * shaped like the real screen. Built from the same example student (`demoDomainMap`) the course
 * path and the Story Stage use, so every example on the site agrees. Responsive markup, not a
 * scaled screenshot, so the text stays readable at phone width. Static: no scroll handlers.
 */
export function SatScoreMockup() {
  const areas = useMemo(
    () =>
      demoDomainMap()
        .map((d) => ({
          domain: d.domain,
          pct: Math.round(d.masterySnapshot.overall.scorePct ?? 0),
          band: d.masterySnapshot.overall.band,
        }))
        // the course orders weakest first
        .sort((a, b) => a.pct - b.pct),
    [],
  );
  const weakest = areas[0];

  return (
    <SurfaceFrame url="mentaratutors.com/sat">
      <div className="space-y-5 bg-background p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <CalloutPin n={1} />
            <p className="text-lg font-bold tracking-tight">Your results, all eight areas</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 px-2.5 py-1 text-xs text-emerald-700 dark:border-emerald-800 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Diagnostic complete
          </span>
        </div>

        <ul className="space-y-3.5" data-testid="results-areas">
          {areas.map((a, i) => (
            <li key={a.domain} data-testid="results-area">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 font-medium">
                  {i === 0 && <CalloutPin n={2} />}
                  {DOMAIN_LABELS[a.domain]}
                </span>
                <span className="shrink-0 tabular-nums">
                  <span className="font-semibold">{a.pct}%</span>
                  <span className="ml-2 text-xs text-muted-foreground">{STUDENT_BAND_LABELS[a.band]}</span>
                </span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className={`h-full rounded-full ${STUDENT_BAND_BAR_CLASSES[a.band]}`} style={{ width: `${a.pct}%` }} />
              </div>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-start gap-2.5">
            <CalloutPin n={3} className="mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Your next step</p>
              <p className="text-sm font-semibold">Practice set: {DOMAIN_LABELS[weakest.domain]}</p>
            </div>
          </div>
          <span className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" aria-hidden>
            Start
          </span>
        </div>
      </div>
    </SurfaceFrame>
  );
}
